import { shallowStringify } from '@gozoki/tools'

import type { WorksConcil } from '@gozoki/api-types'
import { zodResolver } from '@hookform/resolvers/zod'
import { DateTime } from 'luxon'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { frenchNumberSchema } from '../tools/zod-schemas'

export const zWorksConcilForm = z.object({
  // Works concil label
  label: z
    .string()
    .min(3, 'Le libellé du partenatiat doit faire au minimum 3 caractères')
    .max(255, 'Le libellé du partenatiat doit faire au maximum 255 caractères'),

  companyLabel: z.string().nonempty({ message: "Le nom de l'entreprise est requis" }),
  companyCity: z.string().nonempty({ message: "La ville de l'entreprise est requise" }),
  companyAddress: z.string().nullable(),
  companySiren: z.string().nonempty({ message: "Le SIREN de l'entreprise est requis" }),

  // Works concil financial informations
  maxMonthAmountPerEmployee: frenchNumberSchema.nullable(),
  gozokiPercentage: frenchNumberSchema.nullable(),
  worksConcilPercentage: frenchNumberSchema.nullable(),

  // Works concil dates
  startDate: z.string().nonempty({ message: 'La date de début est requise' }),
  endDate: z.string().nonempty({ message: 'La date de fin est requise' }),

  // Works concil users
  employees: z.array(z.number()).nullable().default([]),
  emails: z.array(z.email()).nullable().default([]),
  emailsToAdd: z.array(z.email()).nullable().default([]),

  // Works concil status
  isActive: z.boolean().default(false),
})

export type WorksConcilFormInput = z.input<typeof zWorksConcilForm>
export type WorksConcilFormOutput = z.output<typeof zWorksConcilForm>

// Form hook
export const useWorksConcilForm = (defaultValues?: Partial<WorksConcilFormInput>) => {
  const form = useForm({
    // Validation function
    defaultValues: {
      companyLabel: '',
      companyCity: '',
      companyAddress: '',
      companySiren: '',
      startDate: DateTime.now().toISODate() as string,
      gozokiPercentage: 0,
      worksConcilPercentage: 0,
      maxMonthAmountPerEmployee: null,
      employees: [],
      isActive: false,
      ...defaultValues,
    },
    resolver: zodResolver(zWorksConcilForm),
  })

  return form
}

// Helper : generate precompleted form from existing product data for modification
// Only extract the fields declared in the schema — avoids leaking large preloaded
// relations (e.g. `credits`) into the form state, which would balloon PUT payloads.
export const fromExistingWorksConcil = async (
  worksConcil: WorksConcil,
): Promise<WorksConcilFormOutput> =>
  shallowStringify({
    label: worksConcil.label,
    companyLabel: worksConcil.companyLabel,
    companyCity: worksConcil.companyCity,
    companyAddress: worksConcil.companyAddress,
    companySiren: worksConcil.companySiren,
    maxMonthAmountPerEmployee: worksConcil.maxMonthAmountPerEmployee,
    gozokiPercentage: worksConcil.gozokiPercentage,
    worksConcilPercentage: worksConcil.worksConcilPercentage,
    startDate: worksConcil.startDate,
    endDate: worksConcil.endDate,
    isActive: worksConcil.isActive,
    employees: worksConcil.employees.map((employee) => employee.id),
    emails: worksConcil.emails,
    emailsToAdd: null,
  })
